/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from 'react';
import { Alert, Box, Button, Grid, LinearProgress, Paper, Snackbar, SnackbarCloseReason, TextField, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { VTextField, VForm, useVForm, IVFormErrors, VSwitch, AutoCompleteParceiro, VTextFieldData, VFieldMoney, AutoCompleteProdutor, BasicSelect, VSwitch01, AutoCompletePlanoDeContas } from '../../../shared/forms';
import { FerramentasDeDetalhe, TelaBloqueioSalvando } from '../../../shared/components';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { ContasPagarService } from '../../../shared/services/api/Financeiro/ContasPagar';
import { DialogParcelas } from './DialogParcelas';
import { useNavigation } from '../../../shared/contexts';
import { useBloqueioSalvamento } from '../../../shared/hooks';

interface IFormData {
  emissao: Date;
  vencimento: Date;
  vpagar: number;
  vpago: number;
  vparcela: number;
  vcompra: number;
  parcela: string;
  idplanoconta: string;
  numero: string;
  tipopagamento: string;
  tipo: number;
  desconto: number;
  acrescimo: number;
  historico: string;
  idparceiro: string;
}

const formValidationSchema: yup.ObjectSchema<Omit<IFormData, 'dataoperacao'>> = yup.object().shape({
  idparceiro: yup.string().required(),
  idplanoconta: yup.string().required(),
  tipopagamento: yup.string().required(),
  parcela: yup.string().required(),
  emissao: yup.date().required(),
  vencimento: yup.date().required('O campo é obrigatório')
    .when('$isEditMode', {
      is: false,
      then: () => yup.date().test('valid-date', 'Não se Lança conta já Vencida', function (value) {
        if (!value) {
          return this.createError({ message: 'Data inválida' });
        }
        const currentDate = new Date();
        currentDate.setDate(currentDate.getDate() - 1);
        return value >= currentDate;
      }),
    }),
  historico: yup.string().optional().default(''),
  numero: yup.string().required().max(30),
  vparcela: yup
    .number()
    .required()
    .test(
      'valorparcela',
      'o valor deve ser menor ou igual ao valor total e diferente de zero',
      function (value) {
        const vcompra = Number(this.parent.vcompra || 0);
        const parcela = Number(value || 0);

        const parcelaFixed = Number(parcela.toFixed(2));
        const vcompraFixed = Number(vcompra.toFixed(2));

        return parcelaFixed <= vcompraFixed && parcelaFixed > 0;
      }
    ),
  acrescimo: yup.number().required(),
  desconto: yup
    .number().required()
    .test(
      'desconto-maximo',
      'Desconto maior que o permitido',
      function (value) {
        const parcela = Number(this.parent.vparcela || 0);
        const acrescimo = Number(this.parent.acrescimo || 0);
        const vpago = Number(this.parent.vpago || 0);

        const maximo = parcela + acrescimo - vpago;

        return Number(value || 0) <= maximo;
      }
    ),
  vpago: yup.number().required(),
  vpagar: yup
    .number()
    .required()
    .min(0, 'O valor não pode ser negativo'),
  vcompra: yup.number().required(),


  tipo: yup.number().optional().default(2),
});

export const DetalheDePagar: React.FC = () => {
  const { id = 'novo' } = useParams<'id'>();
  const { goBack } = useNavigation();
  const navigate = useNavigate();
  const { formRef, save, saveAndClose, isSaveAndClose } = useVForm();
  const [isLoading, setIsLoading] = useState(false);
  const { isSaving, progress: saveProgress, iniciar: iniciarSalvamento, concluir: concluirSalvamento, cancelar: cancelarSalvamento } = useBloqueioSalvamento();
  const [descricao, setDescricao] = useState('');
  const [statuss, setStatuss] = useState(false);
  const [statusEntrada, setStatusEntrada] = useState(false);
  const [statusPago, setStatusPgo] = useState(false);
  const [pagas, setPagas] = useState(0);
  const [open, setOpen] = useState(false);
  const [dadosparcelamento, setDadosParcelamento] = useState<IFormData>({} as IFormData);
  const [value, setValue] = useState('');
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  const [openConfirm, setOpenConfirm] = useState(false);


  const parseMoney = (value: string | undefined) => {
    if (!value) return 0;

    return Number(
      value
        .replace(/\./g, '')
        .replace(',', '.')
    );
  };

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
    if (tms === 'success') {
      goBack('/contaspagar')
    }
  };

  const handleDelete = () => {
    setOpenConfirm(true);
  };

  const handleConfirmDelete = () => {
    ContasPagarService.deleteById(id)
      .then(result => {
        if (result instanceof Error) {
          setOpenConfirm(false);
          setTms('error');
          setMsg(result.message);
          setOpenmsg(true);
        } else {
          setOpenConfirm(false);
          setTms('success');
          setMsg('Registro apagado com sucesso!');
          setOpenmsg(true);
        }
      });
  };

  const handleCancelDelete = () => {
    setOpenConfirm(false);
  };

  useEffect(() => {
    if (id !== 'novo') {
      setIsLoading(true);
      ContasPagarService.getById(id)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            setDescricao('Lançamento Contas Pagar');
            formRef.current?.setData(result);
            setPagas(result.vcompra);
            if (result.tipo == 1) {
              setStatusEntrada(true);
            } else {
              setStatusEntrada(false);
            }
            if (result.vcompra > 0) {
              setStatusPgo(true);
            } else {
              setStatusPgo(false);
            }
          }
        });
    } else {
      formRef.current?.setData({
        tipopagamento: 'carteira',
        emissao: new Date(),
        vencimento: new Date(),
        parcela: '1',
        numero: '',
        vparcela: 0,
        vcompra: 0,
        acrescimo: 0,
        vpago: 0,
        desconto: 0,
        vpagar: 0,
        historico: '',

      });
    }
  }, [id]);

  const handleSave = (dados: IFormData) => {
    if (!iniciarSalvamento()) return;

    const validationContext = { isEditMode: id === 'novo' ? false : true };
    formValidationSchema.
      validate(dados, { context: validationContext, abortEarly: false })
      .then((dadosValidados) => {
        setIsLoading(true);
        if (id === 'novo') {
          if (Number(dados.parcela) > 1) {
            const total = dados.vcompra;
            setDadosParcelamento(dados);
            setOpen(true);
            setIsLoading(false);
            cancelarSalvamento();
          } else {
            ContasPagarService
              .create(dadosValidados)
              .then((result) => {
                setIsLoading(false);
                if (result instanceof Error) {
                  cancelarSalvamento();
                  setTms('error');
                  setMsg(result.message);
                  setOpenmsg(true);
                } else {
                  setTms('success');
                  setMsg('Lançamento Realizado com sucesso!');
                  setOpenmsg(true);
                  concluirSalvamento();
                }
              });
          }
        } else {
          ContasPagarService
            .updateById(id, { ...dadosValidados })
            .then((result) => {
              setIsLoading(false);
              if (result instanceof Error) {
                cancelarSalvamento();
                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);
              } else {
                setTms('success');
                setMsg('Lançamento Alterado com sucesso!');
                setOpenmsg(true);
                concluirSalvamento();
              }
            });
        }
      }).catch((errors: yup.ValidationError) => {
        cancelarSalvamento();
        const validationErrors: IVFormErrors = {};
        errors.inner.forEach(error => {
          if (!error.path) return;
          validationErrors[error.path] = error.message;
        });
        formRef.current?.setErrors(validationErrors);
      });
  };

  const handlecalcularParcela = (valor: string | undefined) => {
    const tparcela = parseMoney(valor);

    const data = formRef.current?.getData();

    const acrescimo = Number(data?.acrescimo || 0);
    const desconto = Number(data?.desconto || 0);
    const vpago = Number(data?.vpago || 0);

    const vpagar = Number(
      (tparcela + acrescimo - desconto - vpago).toFixed(2)
    );

    formRef.current?.setFieldValue('vpagar', vpagar);
  };


  const handlecalcularTotal = (valor: string | undefined) => {
    const total = parseMoney(valor);

    const data = formRef.current?.getData();

    const acrescimo = Number(data?.acrescimo || 0);
    const desconto = Number(data?.desconto || 0);
    const vpago = Number(data?.vpago || 0);

    const vpagarCalculado = Number(
      (total + acrescimo - desconto - vpago).toFixed(2)
    );

    const vpagar = Math.max(vpagarCalculado, 0);

    formRef.current?.setFieldValue('vpagar', vpagar);
    formRef.current?.setFieldValue('vparcela', vpagar);
  };


  const handlecalcularAcescimo = (valor: string | undefined) => {
    const acrescimo = parseMoney(valor);

    const data = formRef.current?.getData();

    const parcela = Number(data?.vparcela || 0);
    const desconto = Number(data?.desconto || 0);
    const vpago = Number(data?.vpago || 0);

    const vpagar = Number(
      (parcela + acrescimo - desconto - vpago).toFixed(2)
    );

    formRef.current?.setFieldValue('vpagar', vpagar);
  };

  const handlecalcularDesconto = (valor: string | undefined) => {
    const desconto = parseMoney(valor);

    const data = formRef.current?.getData();

    const parcela = Number(data?.vparcela || 0);
    const acrescimo = Number(data?.acrescimo || 0);
    const vpago = Number(data?.vpago || 0);

    const vpagarCalculado = Number(
      (parcela + acrescimo - desconto - vpago).toFixed(2)
    );

    const vpagar = Math.max(vpagarCalculado, 0);

    formRef.current?.setFieldValue('vpagar', vpagar);
  };
  const handleClose = (newValue?: string) => {
    setOpen(false);

    if (newValue) {
      setValue(newValue);
    }
  };
  return (
    <>
      <TelaBloqueioSalvando
        open={isSaving}
        message="Salvando..."
        progress={saveProgress}
      />
      <LayoutBaseDePagina
      titulo={id === 'novo' ? 'Novo Lançamento Contas Pagar' : descricao}
      barraDeFerramentas={
        <FerramentasDeDetalhe
          textoBotaoNovo='Novo'
          mostrarBotaoSalvarEFechar={false}
          mostrarBotaoSalvarCarregando={isLoading}
          mostrarBotaoSalvarEFecharCarregando={false}
          mostrarBotaoNovo={id !== 'novo'}
          mostrarBotaoApagar={id !== 'novo'}
          mostrarBotaoApagarCarregando={isLoading}
          mostrarBotaoImpressao={false}
          aoClicarEmSalvar={save}
          aoClicarEmSalvarEFechar={saveAndClose}
          aoClicarEmVoltar={() => navigate('/contaspagar')}
          mostrarBotaoVoltarCarregando={isLoading}
          aoClicarEmApagar={() => handleDelete()}
          aoClicarEmNovo={() => navigate('/contaspagar/detalhe/novo')}
          mostrarBotaoNovoCarregando={isLoading}
          disableBotaoApagar={statuss}
          disableBotaoSalvar={statuss || isSaving}
        />
      }
    >
      <VForm ref={formRef} onSubmit={handleSave} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={900} onClose={handleCloseMsg}>
          <Alert
            onClose={handleCloseMsg}
            severity={tms}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {msg}
          </Alert>
        </Snackbar>
        <Snackbar open={openConfirm} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert
            severity="warning"
            variant="filled"
            sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
            action={
              <>
                <Button color="inherit" size="small" onClick={handleConfirmDelete}>
                  Sim
                </Button>
                <Button color="inherit" size="small" onClick={handleCancelDelete}>
                  Nâo
                </Button>
              </>
            }
          >
            Tem certeza que deseja excluir este registro?
          </Alert>
        </Snackbar>
        <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined">

          <Grid container direction="column" padding={2} spacing={2}>

            {isLoading && (
              <Grid item>
                <LinearProgress variant='indeterminate' />
              </Grid>
            )}
            <Grid item>
              <Typography variant='h6'>Dados</Typography>
            </Grid>

            <Grid container item direction="row" spacing={1}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Grid container item direction="row" spacing={2} >
                  <Grid item xs={12} sm={12} md={6} lg={6} xl={6} >
                    <AutoCompleteParceiro
                      isExternalLoading={statuss || statusEntrada || statusPago ? true : isLoading}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                    <AutoCompletePlanoDeContas
                      isExternalLoading={statuss || statusEntrada || statusPago ? true : isLoading}
                    />
                  </Grid>
                  <Grid item xs={8} sm={8} md={2.5} lg={3} xl={3}>
                    <BasicSelect
                      name='tipopagamento'
                      label='Forma de Pagamento'
                      data={[
                        { nameBD: 'boleto', descricao: 'Boleto' },
                        { nameBD: 'carteira', descricao: 'Carteira' },
                        { nameBD: 'deposito', descricao: 'Depósito' },
                        { nameBD: 'debito em conta', descricao: 'Débito em conta' },
                        { nameBD: 'pix', descricao: 'PIX' },
                      ]}
                      disabled={statuss ? true : isLoading}
                    />
                  </Grid>
                  <Grid item xs={4} sm={4} md={1.5} lg={1} xl={1}>
                    {id === 'novo' ? (
                      <BasicSelect
                        name='parcela'
                        label='Parcela'
                        data={[
                          { nameBD: '1', descricao: '1' },
                          { nameBD: '2', descricao: '2' },
                          { nameBD: '3', descricao: '3' },
                          { nameBD: '4', descricao: '4' },
                          { nameBD: '5', descricao: '5' },
                          { nameBD: '6', descricao: '6' },
                          { nameBD: '7', descricao: '7' },
                          { nameBD: '8', descricao: '8' },
                          { nameBD: '9', descricao: '9' },
                          { nameBD: '10', descricao: '10' },
                          { nameBD: '11', descricao: '11' },
                          { nameBD: '12', descricao: '12' },
                        ]}
                        disabled={isLoading}
                      />
                    ) : (
                      <VTextField
                        fullWidth
                        size='small'
                        name='parcela'
                        label='Parcela'
                        disabled={true}
                      />
                    )}
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VTextField
                      fullWidth
                      size='small'
                      name='numero'
                      label='Numero Documento'
                      disabled={statuss || statusEntrada || statusPago ? true : isLoading}
                    />
                  </Grid>

                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VTextFieldData
                      fullWidth
                      size='small'
                      name='emissao'
                      label='Emissão'
                      disabled={statuss || statusEntrada || statusPago ? true : isLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VTextFieldData
                      fullWidth
                      size='small'
                      name='vencimento'
                      label='Vencimento'
                      disabled={statuss ? true : isLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoney
                      fullWidth
                      size='small'
                      name='vcompra'
                      label='Valor Total Compra'
                      calcular={handlecalcularTotal}
                      disabled={statuss || statusEntrada || statusPago ? true : isLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoney
                      fullWidth
                      size='small'
                      name='vparcela'
                      label='Valor Parcela'
                      calcular={handlecalcularParcela}
                      disabled={statuss || statusEntrada || statusPago ? true : isLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoney
                      fullWidth
                      size='small'
                      name='acrescimo'
                      label='Acré. / Juros'
                      calcular={handlecalcularAcescimo}
                      disabled={statuss ? true : isLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoney
                      fullWidth
                      size='small'
                      name='desconto'
                      label='Desconto'
                      calcular={handlecalcularDesconto}
                      disabled={statuss ? true : isLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoney
                      fullWidth
                      size='small'
                      name='vpago'
                      label='Valor Pago'
                      disabled={true}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2} lg={2} xl={2}>
                    <VFieldMoney
                      fullWidth
                      size='small'
                      name='vpagar'
                      label='Valor Pagar'
                      disabled={true}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    <VTextField
                      fullWidth
                      name='historico'
                      disabled={isLoading}
                      multiline
                      rows={3}
                      label='Histório'
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <DialogParcelas
              dados={dadosparcelamento}
              value={''}
              open={open}
              onClose={handleClose}
            />
          </Grid>
        </Box>
      </VForm>
      </LayoutBaseDePagina>
    </>
  );
};